import type { ArabicFontFamily } from '../preferences/types'

// Centralized Arabic font-family map. Mirrors the values used in VerseDisplay
// / SurahHeader / WordByWordDisplay so games match the reader's typography.
export const ARABIC_FONT_FAMILY: Record<ArabicFontFamily, string> = {
  kfgqpc: '"KFGQPC Uthmanic Script HAFS", "Amiri Quran", "Traditional Arabic", serif',
  uthmani: '"Amiri Quran", "Traditional Arabic", serif',
  indopak: '"Noto Nastaliq Urdu", "Traditional Arabic", serif',
  simple: '"Arial", "Helvetica", sans-serif',
}

// Silent-letter teaching marks that should NOT visibly render in clean
// presentations. These code points were designed as tiny superscripts placed
// above a preceding consonant to flag "this letter is silent" (typically a
// silent alif). When the active font lacks the proper combining glyph, the
// browser falls back to rendering the code point at the baseline as a large
// circle, which looks like a stray dot between words. We strip them at the
// boundary so any code path that displays unprocessed Arabic stays clean.
//   U+06DF  Arabic Small High Rounded Zero
//   U+06E0  Arabic Small High Upright Rectangular Zero
// Recitation / waqf marks (U+06D6–U+06DE) are intentionally KEPT — those are
// useful cues for reciters and the Uthmanic fonts render them correctly.
const SILENT_LETTER_MARKS_RE = /[۟۠]/g

/**
 * Strip silent-letter teaching marks from Arabic text. Use on any string that
 * will be rendered in the reader / now-playing / game UIs to defend against
 * source data that didn't pre-strip them (the backend's `textSimple` field
 * was intended to, but several ayahs slip through).
 */
export function stripSilentLetterMarks(text: string | null | undefined): string {
  if (!text) return ''
  return text.replace(SILENT_LETTER_MARKS_RE, '')
}

// Code-point ranges that cover the Arabic script the app actually renders:
//   U+0600..U+06FF  Arabic
//   U+0750..U+077F  Arabic Supplement
//   U+08A0..U+08FF  Arabic Extended-A
//   U+FB50..U+FDFF  Arabic Presentation Forms-A
//   U+FE70..U+FEFF  Arabic Presentation Forms-B
const ARABIC_RANGE_RE = /[؀-ۿݐ-ݿࢠ-ࣿﭐ-﷿ﹰ-﻿]/

/**
 * True if the string contains any Arabic-script character. Used by game UIs
 * to decide whether an answer option should be rendered RTL with the Arabic
 * font (verse-matching questions) or LTR with the default font (translation
 * options, where forcing RTL flips trailing periods/hyphens to the front
 * via the Unicode bidi algorithm).
 */
export function isArabicText(text: string | null | undefined): boolean {
  if (!text) return false
  return ARABIC_RANGE_RE.test(text)
}

/**
 * Build an MUI `sx`-ready font-size object that scales the user's preferred
 * Arabic font size for mobile / tablet / desktop. Same curve VerseDisplay uses
 * so the games look consistent with the reader.
 *
 * @param fontSize desktop pixel size (e.g. user's `prefs.fontSize`)
 * @param scale    optional uniform multiplier (e.g. 0.6 for short snippets)
 */
export function responsiveArabicFontSize(fontSize: number, scale = 1) {
  const desktop = Math.round(fontSize * scale)
  return {
    xs: `${Math.max(24, Math.round(desktop * 0.55))}px`,
    sm: `${Math.max(28, Math.round(desktop * 0.75))}px`,
    md: `${desktop}px`,
  }
}
